import {
  GetVariationsOverviewMetricsResponse,
  VariationsMetricsByWeekRecord
} from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/ContentServerProxy/types';
import { ChartDataAdapter } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/components/Summary/Visualizations/adapters/types';
import {
  VariationsFilter,
  VariationsGaugeData,
  VariationsPerformanceGaugeData,
  VariationPerformanceMetrics
} from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/helpers/contentVariationUtils';
import { calculatePercentChange } from 'src/utils/app';
import { METRICTYPE } from 'src/utils/entityDefinitions';

const MAX_PERCENTAGE = 1.0;
const DECIMAL_TO_PERCENTAGE_MULTIPLIER = 100;

const parseMetricValue = (value: string): number => {
  const parsed = parseFloat(value);
  return isNaN(parsed) ? 0 : Math.min(parsed, MAX_PERCENTAGE);
};

export class VariationsGaugeAdapter implements ChartDataAdapter<VariationsPerformanceGaugeData> {
  private readonly mainVariationsMetricsRecords?: VariationsMetricsByWeekRecord[];
  private readonly comparisonVariationsMetricsRecords?: VariationsMetricsByWeekRecord[];

  constructor(
    mainVariationsMetricsRecords?: VariationsMetricsByWeekRecord[],
    comparisonVariationsMetricsRecords?: VariationsMetricsByWeekRecord[],
    variationsOverviewMetricsResponse?: GetVariationsOverviewMetricsResponse['data']
  ) {
    this.mainVariationsMetricsRecords = mainVariationsMetricsRecords;
    this.comparisonVariationsMetricsRecords = comparisonVariationsMetricsRecords;
  }

  private getMetricsFromRecords(records?: VariationsMetricsByWeekRecord[]): VariationPerformanceMetrics {
    const latestRecord = records?.[records.length - 1];

    return {
      childVariationRate: Math.min(latestRecord?.childVariationRate || 0, MAX_PERCENTAGE),
      accuracyRate: Math.min(latestRecord?.childAccuracy || 0, MAX_PERCENTAGE),
      pageDensity: Math.min(latestRecord?.pageDensity || 0, MAX_PERCENTAGE),
      parentPageAccuracy: Math.min(latestRecord?.parentPageAccuracy || 0, MAX_PERCENTAGE),
      singleSkuCount: latestRecord?.singleSkuCount || 0,
      variatedSkuCount: latestRecord?.variatedSkuCount || 0,
      correctChildCount: latestRecord?.correctChildCount || 0,
      incorrectChildCount: latestRecord?.incorrectChildCount || 0,
      inaccurateParentCount: latestRecord?.inaccurateParentCount || 0
    };
  }

  private getLatestMetrics(): VariationPerformanceMetrics {
    return this.getMetricsFromRecords(this.mainVariationsMetricsRecords);
  }

  private getComparisonMetrics(): VariationPerformanceMetrics {
    return this.getMetricsFromRecords(this.comparisonVariationsMetricsRecords);
  }

  private hasComparisonData(): boolean {
    return !!this.comparisonVariationsMetricsRecords?.length;
  }

  private createGaugeDataItem(
    value: number,
    filterKey: VariationsFilter,
    percentage?: number,
    change?: number,
    numerator?: number,
    denominator?: number,
    absoluteChange?: number
  ): VariationsGaugeData {
    // Determine the appropriate metric formatter based on the filter type
    const getMetricFormatter = () => {
      switch (filterKey) {
        case VariationsFilter.ChildVariationRate:
        case VariationsFilter.ChildAccuracy:
        case VariationsFilter.ParentPageAccuracy:
        case VariationsFilter.PageDensity:
          return {
            showFullValue: false,
            decimalPlaces: 1,
            valueMetricType: METRICTYPE.PERCENT
          };
        default:
          return {
            showFullValue: false,
            decimalPlaces: 1,
            valueMetricType: METRICTYPE.VOLUME
          };
      }
    };

    return {
      value: percentage ?? value,
      filterKey,
      valueMetricFormatter: getMetricFormatter(),
      ...(percentage !== undefined && { percentage }),
      ...(change !== undefined && { change }),
      ...(absoluteChange !== undefined && { absoluteChange }),
      ...(numerator !== undefined && { numerator }),
      ...(denominator !== undefined && { denominator })
    };
  }

  private createComparisonGaugeItem(
    value: number,
    filterKey: VariationsFilter,
    comparisonValue: number,
    isPercentageMetric: boolean = false,
    numerator?: number,
    denominator?: number
  ): VariationsGaugeData {
    const percentChange = this.hasComparisonData() ? calculatePercentChange(value, comparisonValue) : 0;
    const absoluteChange = this.hasComparisonData() ? value - comparisonValue : 0;
    const displayValue = isPercentageMetric ? value * DECIMAL_TO_PERCENTAGE_MULTIPLIER : value;
    return this.createGaugeDataItem(
      displayValue,
      filterKey,
      isPercentageMetric ? value : undefined,
      percentChange,
      numerator,
      denominator,
      absoluteChange
    );
  }

  private createSimpleGaugeItem(value: number, filterKey: VariationsFilter): VariationsGaugeData {
    return this.createGaugeDataItem(value, filterKey);
  }

  transform(): VariationsPerformanceGaugeData {
    const latestMetrics = this.getLatestMetrics();
    const comparisonMetrics = this.getComparisonMetrics();
    const latestRecord = this.mainVariationsMetricsRecords?.[this.mainVariationsMetricsRecords.length - 1];

    const gaugeData: VariationsGaugeData[] = [
      this.createComparisonGaugeItem(
        latestMetrics.childVariationRate,
        VariationsFilter.ChildVariationRate,
        comparisonMetrics.childVariationRate,
        true,
        latestRecord?.childVariationRateNumerator,
        latestRecord?.childVariationRateDenominator
      ),
      this.createComparisonGaugeItem(
        latestMetrics.singleSkuCount,
        VariationsFilter.SingleSkuPages,
        comparisonMetrics.singleSkuCount,
        false
      ),
      this.createComparisonGaugeItem(
        latestMetrics.variatedSkuCount,
        VariationsFilter.VariatedSkuPages,
        comparisonMetrics.variatedSkuCount,
        false
      ),
      this.createComparisonGaugeItem(
        latestMetrics.accuracyRate,
        VariationsFilter.ChildAccuracy,
        comparisonMetrics.accuracyRate,
        true,
        latestRecord?.childAccuracyNumerator,
        latestRecord?.childAccuracyDenominator
      ),
      this.createComparisonGaugeItem(
        latestMetrics.correctChildCount,
        VariationsFilter.CorrectChildSkus,
        comparisonMetrics.correctChildCount,
        false
      ),
      this.createComparisonGaugeItem(
        latestMetrics.incorrectChildCount,
        VariationsFilter.IncorrectChildSkus,
        comparisonMetrics.incorrectChildCount,
        false
      ),
      this.createComparisonGaugeItem(
        latestMetrics.parentPageAccuracy,
        VariationsFilter.ParentPageAccuracy,
        comparisonMetrics.parentPageAccuracy,
        true,
        latestRecord?.parentPageAccuracyNumerator,
        latestRecord?.parentPageAccuracyDenominator
      ),
      this.createComparisonGaugeItem(
        latestMetrics.inaccurateParentCount,
        VariationsFilter.InaccurateParentPages,
        comparisonMetrics.inaccurateParentCount,
        false
      ),
      this.createComparisonGaugeItem(
        latestMetrics.pageDensity,
        VariationsFilter.PageDensity,
        comparisonMetrics.pageDensity,
        true,
        latestRecord?.pageDensityNumerator,
        latestRecord?.pageDensityDenominator
      )
    ];

    return {
      gaugeData,
      variationsMetricsRecords: this.mainVariationsMetricsRecords
    };
  }
}
